/**
 * Blog Portfolio Gallery — custom element
 *
 * Manages tab switching, year filtering, and carousel scrolling for the
 * sections/blog-portfolio-gallery.liquid section.
 *
 * How it works:
 *   - All article cards are pre-rendered server-side and hidden via CSS.
 *   - Each card carries data-blog (blog handle) and data-year (year metafield).
 *   - Category tab clicks filter visible cards by blog handle.
 *   - Year filter buttons narrow results further by year.
 *   - When year = "all", cards are shown grouped by year heading in a wrapped grid.
 *   - When year is specific, the horizontal carousel + prev/next arrows are active.
 */

class BlogPortfolioGallery extends HTMLElement {
  // Lifecycle

  connectedCallback() {
    // connectedCallback fires when the element's opening tag is parsed —
    // before any child elements exist in the DOM. Deferring with setTimeout
    // ensures all children are available when init() runs.
    setTimeout(() => this.init(), 0);
  }

  init() {
    /** @type {string|null} Handle of the currently active blog tab */
    this.activeBlogHandle = null;

    /** @type {string} Currently selected year ("all" or "YYYY") */
    this.activeYear = "all"; // overridden below if year buttons exist

    /** @type {boolean} Whether the year filter feature is enabled in theme settings */
    this.yearFilterEnabled = this.dataset.yearFilterEnabled === "true";

    /** @type {string} Navigation mode: "gallery" (slider) or "grid" */
    this.navMode = this.dataset.navMode ?? "gallery";

    // Collect references to interactive elements
    this.tabButtons = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll(".blog-portfolio-gallery__tab"))
    );
    this.articleCards = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll(".card-article"))
    );
    this.yearButtons = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll(".blog-portfolio-gallery__year-btn"))
    );
    this.grid = this.querySelector(".blog-portfolio-gallery__carousel");
    this.prevButtons = /** @type {HTMLButtonElement[]} */ (
      Array.from(this.querySelectorAll(".blog-portfolio-gallery__prev"))
    );
    this.nextButtons = /** @type {HTMLButtonElement[]} */ (
      Array.from(this.querySelectorAll(".blog-portfolio-gallery__next"))
    );
    this.yearPrevButton = /** @type {HTMLButtonElement|null} */ (
      this.querySelector(".blog-portfolio-gallery__year-prev")
    );
    this.yearNextButton = /** @type {HTMLButtonElement|null} */ (
      this.querySelector(".blog-portfolio-gallery__year-next")
    );
    this.yearNavEl = /** @type {HTMLElement|null} */ (
      this.querySelector(".blog-portfolio-gallery__year-nav")
    );
    this.footerEl = /** @type {HTMLElement|null} */ (
      this.querySelector(".blog-portfolio-gallery__footer")
    );
    this.footerDividerEl = /** @type {HTMLElement|null} */ (
      this.querySelector(".blog-portfolio-gallery__footer-divider")
    );
    this.filteredEmpty = /** @type {HTMLElement|null} */ (
      this.querySelector(".blog-portfolio-gallery__filtered-empty")
    );
    this.carouselTitle = /** @type {HTMLElement|null} */ (
      this.querySelector(".blog-portfolio-gallery__carousel-title")
    );

    // First year button is always "All" (data-year="all"), keep default
    const firstYearBtn = this.yearButtons?.[0];
    if (firstYearBtn) {
      this.activeYear = firstYearBtn.dataset.year ?? "all";
    }

    this.attachEventListeners();
    this.activateFirstTab();
  }

  // Initialisation helpers

  /**
   * Binds click handlers to tab buttons, year buttons, and navigation arrows.
   */
  attachEventListeners() {
    this.tabButtons?.forEach((tab) => {
      tab.addEventListener("click", () => this.handleTabClick(tab));
    });

    this.yearButtons?.forEach((btn) => {
      btn.addEventListener("click", () => this.handleYearClick(btn));
    });

    this.prevButtons?.forEach((btn) => {
      btn.addEventListener("click", () => this.scrollPrev());
    });

    this.nextButtons?.forEach((btn) => {
      btn.addEventListener("click", () => this.scrollNext());
    });

    if (this.yearPrevButton) {
      this.yearPrevButton.addEventListener("click", () => this.navigateYear(-1));
    }

    if (this.yearNextButton) {
      this.yearNextButton.addEventListener("click", () => this.navigateYear(1));
    }

    if (this.navMode === "gallery" && this.grid) {
      this.grid.addEventListener("scroll", () => this.updateNavigationState(), {
        passive: true,
      });
    }
  }

  /**
   * Activates the tab requested via the `?blog=` URL param (e.g. when arriving
   * from a breadcrumb link), falling back to the first tab so visitors always
   * see content.
   */
  activateFirstTab() {
    const requestedHandle = new URLSearchParams(window.location.search).get("blog");
    const requestedTab = requestedHandle
      ? this.tabButtons?.find((tab) => tab.dataset.blog === requestedHandle)
      : null;
    const targetTab = requestedTab ?? this.tabButtons?.[0];
    if (!targetTab) return;

    this.tabButtons?.forEach((tab) => tab.classList.remove("is-active"));
    targetTab.classList.add("is-active");

    this.activeBlogHandle = targetTab.dataset.blog ?? null;
    this.showActiveCards();
  }

  // Tab switching

  /**
   * Handles a category tab click.
   *
   * @param {HTMLElement} clickedTab
   */
  handleTabClick(clickedTab) {
    if (!this.tabButtons) return;

    this.tabButtons.forEach((tab) => tab.classList.remove("is-active"));
    clickedTab.classList.add("is-active");

    this.activeBlogHandle = clickedTab.dataset.blog ?? null;
    this.showActiveCards();
  }

  /**
   * Handles a year filter button click.
   *
   * @param {HTMLElement} clickedBtn
   */
  handleYearClick(clickedBtn) {
    this.yearButtons?.forEach((btn) => btn.classList.remove("is-active"));
    clickedBtn.classList.add("is-active");

    this.activeYear = clickedBtn.dataset.year ?? "all";
    this.showActiveCards();
  }

  // Carousel

  /**
   * Whether cards should be grouped by year into a wrapped, multi-row layout.
   * Only applies outside gallery (horizontal slider) mode: in gallery mode the
   * "All" view stays a single horizontal carousel so the prev/next arrows work.
   *
   * @returns {boolean}
   */
  isGroupedView() {
    return Boolean(this.navMode !== "gallery" && this.yearFilterEnabled);
  }

  /**
   * Returns all cards that match the active blog AND active year filter.
   *
   * @returns {HTMLElement[]}
   */
  getActiveCards() {
    const allCards = this.articleCards ?? [];
    let cards =
      this.activeBlogHandle === "all"
        ? allCards
        : allCards.filter(
            (card) => card.dataset.blog === this.activeBlogHandle,
          );

    if (this.yearFilterEnabled && this.activeYear !== "all") {
      cards = cards.filter(
        (card) => card.dataset.year === this.activeYear,
      );
    }

    return cards;
  }

  /**
   * Hides year filter buttons (and their separators) that have no articles
   * under the currently active category, so merchants only ever see years
   * with actual content. Falls back to "All" if the currently active year
   * disappears for the newly selected category.
   */
  updateYearButtonsVisibility() {
    if (!this.yearFilterEnabled || !this.yearButtons) return;

    const allCards = this.articleCards ?? [];
    const cardsInCategory =
      this.activeBlogHandle === "all"
        ? allCards
        : allCards.filter((card) => card.dataset.blog === this.activeBlogHandle);

    const yearsInCategory = new Set(
      cardsInCategory.map((card) => card.dataset.year ?? "").filter(Boolean),
    );

    let activeYearStillVisible = true;

    this.yearButtons.forEach((btn) => {
      const year = btn.dataset.year ?? "all";
      const isVisible = year === "all" || yearsInCategory.has(year);

      btn.classList.toggle("is-hidden", !isVisible);

      const sep = btn.previousElementSibling;
      if (sep?.classList.contains("blog-portfolio-gallery__year-sep")) {
        sep.classList.toggle("is-hidden", !isVisible);
      }

      if (!isVisible && year === this.activeYear) {
        activeYearStillVisible = false;
      }
    });

    if (!activeYearStillVisible) {
      const allYearBtn = this.yearButtons.find((btn) => btn.dataset.year === "all");
      this.yearButtons.forEach((btn) => btn.classList.remove("is-active"));
      allYearBtn?.classList.add("is-active");
      this.activeYear = "all";
    }
  }

  /**
   * Shows cards matching the active filters, toggles grouped mode, and resets
   * the carousel scroll position.
   */
  showActiveCards() {
    this.updateYearButtonsVisibility();

    // Remove any previously injected year headings and dividers before re-filtering
    this.grid
      ?.querySelectorAll(".blog-portfolio-gallery__year-heading, .blog-portfolio-gallery__year-divider")
      .forEach((h) => h.remove());

    this.articleCards?.forEach((card) => card.classList.remove("is-visible"));
    this.getActiveCards().forEach((card) => card.classList.add("is-visible"));

    const isGrouped = this.isGroupedView();

    if (this.grid) {
      this.grid.classList.toggle("is-grouped", isGrouped);
      this.grid.scrollLeft = 0;
    }

    if (isGrouped) {
      this.insertYearHeadings();
    }

    if (this.carouselTitle) {
      this.carouselTitle.textContent = this.activeYear !== "all" ? (this.activeYear ?? null) : null;
    }

    const hasResults = this.getActiveCards().length > 0;

    if (this.grid) this.grid.classList.toggle("is-hidden", !hasResults);
    if (this.filteredEmpty) this.filteredEmpty.classList.toggle("is-visible", !hasResults);

    this.updateNavigationState();
    this.updateYearNavState();
  }

  /**
   * Groups currently visible cards by year (newest first), physically
   * reordering them in the DOM so every card sits contiguously under its
   * year heading. Cards are pre-rendered in publish order, so without this
   * reordering the headings would be detached from their year's cards.
   * Only called when activeYear === "all" and yearFilterEnabled is true.
   */
  insertYearHeadings() {
    const grid = this.grid;
    if (!grid) return;

    const visibleCards = (this.articleCards ?? []).filter((card) =>
      card.classList.contains("is-visible"),
    );

    // Collect unique years present in visible cards, sorted descending (newest first)
    const years = [
      ...new Set(
        visibleCards
          .map((card) => card.dataset.year ?? "")
          .filter(Boolean),
      ),
    ].sort().reverse();

    years.forEach((year, index) => {
      const cardsForYear = visibleCards.filter(
        (card) => card.dataset.year === year,
      );
      if (cardsForYear.length === 0) return;

      if (index > 0) {
        const divider = document.createElement("div");
        divider.className = "blog-portfolio-gallery__year-divider";
        grid.appendChild(divider);
      }

      const heading = document.createElement("h2");
      heading.className = "blog-portfolio-gallery__year-heading";
      heading.textContent = year;
      grid.appendChild(heading);

      // Re-append this year's cards so they follow their heading in order
      cardsForYear.forEach((card) => grid.appendChild(card));
    });
  }

  /**
   * Enables or disables prev / next buttons based on scroll position.
   * Both buttons are disabled in grouped (year = All) mode.
   */
  updateNavigationState() {
    if (!this.grid) return;

    const isGrouped = this.isGroupedView();

    if (isGrouped) {
      this.prevButtons?.forEach((btn) => { btn.disabled = true; });
      this.nextButtons?.forEach((btn) => { btn.disabled = true; });
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = this.grid;

    this.prevButtons?.forEach((btn) => {
      btn.disabled = scrollLeft <= 0;
    });

    this.nextButtons?.forEach((btn) => {
      btn.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
    });
  }

  /**
   * Advances the active year by +1 or -1 through the available year buttons.
   *
   * @param {1|-1} direction
   */
  navigateYear(direction) {
    if (!this.yearButtons || this.yearButtons.length === 0) return;

    const currentIndex = this.yearButtons.findIndex(
      (btn) => btn.dataset.year === this.activeYear,
    );
    const nextIndex = currentIndex + direction;

    const target = this.yearButtons[nextIndex];
    if (nextIndex < 0 || !target) return;

    this.handleYearClick(target);
  }

  /**
   * Disables year-nav arrow buttons when the active year is at a boundary.
   */
  updateYearNavState() {
    // Hide the footer (year-nav + divider) when viewing all blogs or all years —
    // navigating by year only makes sense on a single blog with a specific year selected.
    const hideFooter = this.activeBlogHandle === "all" || this.activeYear === "all";
    if (this.footerEl) this.footerEl.classList.toggle("is-hidden", hideFooter);
    if (this.footerDividerEl) this.footerDividerEl.classList.toggle("is-hidden", hideFooter);

    if (!this.yearPrevButton && !this.yearNextButton) return;
    if (!this.yearButtons || this.yearButtons.length === 0) return;

    const currentIndex = this.yearButtons.findIndex(
      (btn) => btn.dataset.year === this.activeYear,
    );

    if (this.yearPrevButton) {
      this.yearPrevButton.disabled = currentIndex <= 0;
    }
    if (this.yearNextButton) {
      this.yearNextButton.disabled = currentIndex >= this.yearButtons.length - 1;
    }
  }

  /**
   * Scrolls the carousel one viewport-width to the left.
   */
  scrollPrev() {
    if (this.grid) this.grid.scrollLeft -= this.grid.clientWidth;
  }

  /**
   * Scrolls the carousel one viewport-width to the right.
   */
  scrollNext() {
    if (this.grid) this.grid.scrollLeft += this.grid.clientWidth;
  }
}

// Guard against the element being defined twice when Shopify re-renders sections
if (!customElements.get("blog-portfolio-gallery")) {
  customElements.define("blog-portfolio-gallery", BlogPortfolioGallery);
}
